import axios, { AxiosResponse } from "axios";
import { MaintenanceUrl } from "./AUrl";
import { ApiResponse } from "../auth/login";

/*
Where now sits relative to the scheduled maintenance window.
 */
export type MaintenanceState = "none" | "upcoming" | "active";

/*
The maintenance window as the backend reports it.

@state: whether there is a window, it is coming, or it is open.
@starts_at: when the window opens, null when there is nothing to announce.
@ends_at: when the window closes, null when there is nothing to announce.
 */
export interface MaintenanceStatus {
    state: MaintenanceState;
    starts_at: string | null;
    ends_at: string | null;
}

const CACHE_KEY = "cm.maintenance.status";
const CACHE_TTL_MS = 5 * 60 * 1000;

/*
The cached status, if one was stored recently enough to still be worth using.

An entry that will not parse is ignored rather than trusted: a bad cache must not
be able to suppress the banner.
 */
function cached(): MaintenanceStatus | null {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const { at, status } = JSON.parse(raw);
        if (typeof at !== "number" || Date.now() - at > CACHE_TTL_MS)
            return null;
        return status as MaintenanceStatus;
    } catch {
        return null;
    }
}

/*
Fetches the maintenance status. Needs no token: it has to work on the login page.

Served from a short-lived cache when there is one, so moving between entry
screens does not refetch. There is no polling: the banner only renders on entry
screens, and a mount is already the moment the answer could have changed.
 */
export async function getMaintenanceStatus(): Promise<
    ApiResponse<MaintenanceStatus>
> {
    const hit = cached();
    if (hit) {
        return { status: 200, data: hit };
    }
    const url = new MaintenanceUrl().status;
    let response: AxiosResponse<any>;
    try {
        response = await axios.get<MaintenanceStatus>(url, {
            validateStatus: () => true,
        });
    } catch (error) {
        return { status: 500, error: "Network or other error occurred" };
    }

    if (response.status === 200) {
        const status = response.data as MaintenanceStatus;
        try {
            localStorage.setItem(
                CACHE_KEY,
                JSON.stringify({ at: Date.now(), status }),
            );
        } catch {
            // A full or unavailable localStorage costs a refetch, not the banner.
        }
        return { status: response.status, data: status };
    }
    return { status: response.status, error: response.data as string };
}
